import { apiErrorMessage } from '@agos/api-client'
import { usePostPaymentsInitiate } from '@agos/api-client/payments'
import {
  OrderPlacedContext,
  canRetryPayment,
  hasPointsToShow,
  retryOutcome,
  takeOrderHandoff,
} from '@agos/customer-core'
import { T, centsToEuros, formatCount, tt } from '@agos/shared'
import { CheckCircle2, ChevronRight, Clock, Home, RefreshCw, Star, XCircle } from 'lucide-react'
import { useContext, useState } from 'react'
import { Link } from 'wouter'

import { paymentErrorReason } from './payment-error'

/**
 * The pages a customer lands on rather than navigates to - §9.793 added the third, for a payment still pending.
 *
 * All are **order-blind**: `OnlinePaymentService.Landing` is two bare paths with no query string, so
 * neither can name the order. Success reconstructs what it can from the note the checkout left in
 * `sessionStorage` and says nothing when that is gone. They live in this module because the note is the
 * checkout's — nobody else writes it.
 *
 * The layout is legacy's: a centred card with a coloured bar across the top, a large circular icon, and
 * a *what happens next* panel. The first AGOS draft was a bare heading and two links.
 */

const CARD =
  'bg-card border border-border shadow-2xl rounded-3xl p-8 md:p-12 max-w-md w-full text-center relative overflow-hidden'
const HOME_LINK =
  'inline-flex items-center justify-center gap-2 w-full bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground font-bold py-3.5 rounded-xl transition-colors'

export function SuccessPage() {
  // Read once, on mount, because reading removes it — a second read during a re-render would find
  // nothing and the banner would vanish under the customer.
  const [handoff] = useState(takeOrderHandoff)
  // §9.779 - what another module draws under the thanks; the games, today. Handed down by the shell, so this
  // module imports none of them.
  const afterOrder = useContext(OrderPlacedContext)

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 overflow-x-hidden">
      <div className={`${CARD} space-y-6`} data-success>
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-amber-300" />

        <div>
          <div className="mx-auto w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-display font-bold mb-4">
            <T>Ďakujeme!</T>
          </h1>
          <p className="text-muted-foreground">
            <T>Vaša objednávka bola úspešne prijatá a už na nej pracujeme.</T>
          </p>
          {handoff.orderNumber !== null && (
            <p className="text-sm font-semibold mt-2" data-success-order={handoff.orderNumber}>
              {handoff.orderNumber}
            </p>
          )}
        </div>

        {hasPointsToShow(handoff) && (
          <div
            className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-left"
            data-points-earned
          >
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="font-bold text-sm text-foreground">
                <T>Na váš účet sme pripísali</T>{' '}
                <span className="text-amber-600">
                  {formatCount(handoff.points ?? 0)} <T>bodov</T>
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                <T>Vernostné body môžete využiť pri ďalšej objednávke</T>
              </p>
            </div>
          </div>
        )}

        {/* §9.779, owner O138f - *Klikni a vyhraj* and *Stierací los* under the thanks, for an order this tab
            has just placed: every web order leaves its id, cash or card. Each surface decides the rest -
            the games draw nothing for a guest, for a game switched off or for one already played today.
            Legacy's own card asked `GET /mini-game/scratch-eligible`, which its server never had, so it
            never appeared; these play the profile's game through the profile's endpoints. */}
        {handoff.orderId !== null && afterOrder.length > 0 && (
          <div className="space-y-2 text-left" data-order-placed>
            {afterOrder.map((surface) => {
              const Surface = surface.component
              return <Surface key={surface.name} />
            })}
          </div>
        )}

        <div className="bg-secondary/50 rounded-2xl p-4 text-left">
          <p className="text-sm font-semibold mb-1">
            <T>Čo sa bude diať ďalej?</T>
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mt-3">
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary" /> <T>Kuchyňa pripravuje vaše jedlo</T>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary" />{' '}
              <T>V prípade potreby vás budeme kontaktovať</T>
            </li>
          </ul>
        </div>

        <div className="space-y-2">
          <Link href="/" data-to-home className={HOME_LINK}>
            <Home className="w-4 h-4" />
            <T>Naspäť na menu</T>
          </Link>
          <Link href="/moj-profil" data-to-profile className="block text-sm text-primary hover:underline">
            <T>Moje objednávky</T>
          </Link>
        </div>
      </div>
    </div>
  )
}

export function PaymentErrorPage() {
  /* §9.470 - the bank's own reason, when the checkout had one to pass on.
   *
   * Read off `window.location.search` rather than a router hook because these two landing paths are
   * rendered outside the checkout's own routes; `paymentErrorReason` is the parser and is tested
   * directly. Absent when the **bank** sent the customer here, which is the original case and where
   * the paragraph below is the whole message. */
  const reason = paymentErrorReason(
    typeof window === 'undefined' ? '' : window.location.search,
  )

  /* §9.505, FB-795 - read ONCE, for `SuccessPage`'s own recorded reason: `takeOrderHandoff` removes each key,
   * so a second read during a re-render finds nothing and the button would vanish under the customer. */
  const [handoff] = useState(takeOrderHandoff)
  const initiatePayment = usePostPaymentsInitiate()
  const [retryFailed, setRetryFailed] = useState<string | null>(null)

  const retry = async (): Promise<void> => {
    setRetryFailed(null)
    try {
      const session = await initiatePayment.mutateAsync({
        data: {
          orderId: handoff.orderId ?? 0,
          orderNumber: handoff.orderNumber ?? '',
          amount: centsToEuros(handoff.totalCents ?? 0),
        },
      })
      const outcome = retryOutcome(session)
      if (outcome.kind === 'redirect') {
        window.location.assign(outcome.url)
        return
      }
      // §10A.166's fourth outcome - an answer with no URL - is a refusal like any other.
      setRetryFailed(tt('Platobnú bránu sa nepodarilo otvoriť. Skúste to prosím znova.'))
    } catch (failure) {
      /* The server's own sentence where there is one - §10A.166 makes an open breaker, a refused token
       * exchange, a refused create and an answer with no URL four different messages, and §9.470 exists
       * because folding them into one threw that away. The fallback is only for a network failure. */
      setRetryFailed(
        apiErrorMessage(failure, tt('Platbu sa nepodarilo spustiť. Skúste to prosím znova.')),
      )
    }
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 overflow-x-hidden">
      <div className={CARD} data-payment-error>
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-destructive to-red-400" />

        <div className="mx-auto w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10" />
        </div>

        <h1 className="text-3xl font-display font-bold mb-4">
          <T>Platba neúspešná</T>
        </h1>
        {/* True by construction - the bank sent the customer here - and true about the order too, because
            the checkout POSTs `/orders` before `/payments/initiate`, so it exists by the time the bank is
            involved. */}
        <p className="text-muted-foreground mb-6">
          <T>
            Platba sa nepodarila alebo bola zrušená. Vaša objednávka bola prijatá, ale zostáva nezaplatená.
          </T>
        </p>
        {/* Below the sentence that matters rather than instead of it: *your order was received and is
            unpaid* is what the customer needs, and the bank's wording is why. */}
        {reason !== '' && (
          <p className="text-sm text-muted-foreground/80 mb-6" data-payment-error-reason>
            {reason}
          </p>
        )}

        <div className="bg-secondary/50 rounded-2xl p-4 mb-8 text-left">
          <p className="text-sm font-semibold mb-1">
            <T>Čo môžete urobiť?</T>
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mt-3">
            {/* Legacy's first bullet advises placing the order again, which produces a *second* order
                for somebody who already has one waiting unpaid. Replaced rather than reworded: what is
                left is both true and useful. */}
            <li className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary shrink-0" />{' '}
              <T>Zaplaťte pri prevzatí - v hotovosti alebo kartou</T>
            </li>
            {/* Legacy's second bullet, verbatim. It had been dropped and replaced with *Ak si nie ste
                istí, zavolajte nám* - a sentence nowhere in legacy, added without a reason beside a
                replacement that had one. FB-C66. */}
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary shrink-0" />{' '}
              <T>Vyberte iný spôsob platby (hotovosť / terminál)</T>
            </li>
          </ul>
        </div>

        <div className="space-y-2">
          {/* §9.505 - above the fallbacks, because it is the only one of the three that still takes the
              money. Offered only with everything `initiate` needs; the server refuses a second session on an
              order already paid (§10A.262), so pressing it twice cannot double-charge. */}
          {canRetryPayment(handoff) && (
            <button
              type="button"
              onClick={() => void retry()}
              disabled={initiatePayment.isPending}
              data-retry-payment
              className="inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground hover:opacity-90 font-bold py-3.5 rounded-xl transition-opacity disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <T>Skúsiť platbu znova</T>
            </button>
          )}
          {retryFailed !== null && (
            <p className="text-sm text-destructive" data-retry-failed>
              {retryFailed}
            </p>
          )}
          <Link href="/" data-to-home className={HOME_LINK}>
            <Home className="w-4 h-4" />
            <T>Naspäť na menu</T>
          </Link>
          <Link href="/moj-profil" data-to-profile className="block text-sm text-primary hover:underline">
            <T>Moje objednávky</T>
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * Where the server sends a customer whose payment the gateway has not finished deciding - FRONTEND.md §9.793.
 *
 * **`OnlinePaymentService.Landing.PENDING` has redirected to `/payment-pending` since the first port, and nothing
 * served it**, so a customer landed on *Stránka sa nenašla* - legacy redirects to the same path and has no page for
 * it either. TatraPay sends a QR transfer still on its way here; GP webpay sends a customer who pressed back or
 * reloaded its page (`PRCODE 20`) while the payment is still open, which a card customer meets far more often.
 *
 * **What it says is what happens next**: the stale-payment sweep asks the gateway every minute and settles the
 * order either way, so the customer is told not to pay again - a second payment on an order whose first one is
 * about to land is exactly what the sweep would then have to refund.
 */
export function PaymentPendingPage() {
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 overflow-x-hidden">
      <div className={`${CARD} space-y-6`} data-payment-pending>
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 to-amber-200" />
        <div>
          <div className="mx-auto w-20 h-20 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mb-6">
            <Clock className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-display font-bold mb-4">
            <T>Platba sa ešte overuje</T>
          </h1>
          <p className="text-muted-foreground">
            <T>Banka zatiaľ nepotvrdila, či platba prešla. Keď ju potvrdí, objednávka sa prijme sama - nemusíte nič robiť ani platiť znova.</T>
          </p>
          <p className="text-muted-foreground text-sm mt-3">
            <T>Ak platba neprejde, peniaze vám nestrhneme a objednávka sa neprijme.</T>
          </p>
        </div>
        <Link href="/" className={HOME_LINK} data-payment-pending-home>
          <Home className="w-4 h-4" /> <T>Späť do menu</T>
        </Link>
      </div>
    </div>
  )
}
